import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Box, Paper, Typography } from "@mui/material";

import CommentForm, { CommentFormRef } from "./CommentForm";
import CommentList from "./CommentList";
import type { Comment } from "./VTBCommentTypes";
import api from "../../api/endpoints";
import { useAppSelector } from "../../store/hooks";
import { useDispatch } from "react-redux";
import { updateNCGBrowserCommentsCount } from "../../store/slice/ncgDetailsSlice";

// Adjust the import path based on your project

interface CommentsSectionProps {
  articleId?: number | string;
  focusCommentInput?: boolean;
  // isVisible: boolean;
  // customerId?: number | string;
  // consortiaId?: number | string;
  // usermasterId?: number | string;
}

const VTBCommentsSection: React.FC<CommentsSectionProps> = ({
  articleId,
  focusCommentInput = false,
  // isVisible,
  // customerId,
  // consortiaId,
  // usermasterId,
}) => {
  const customerDetails = useAppSelector(
    (state) => state.customer.customerDetails,
  );
  const customerId =
    sessionStorage.getItem("informaticscustomer_id") ||
    customerDetails?.informaticscustomer_id;
  const usermasterId = sessionStorage.getItem("user_id");
  const consortiaId = customerDetails?.consortiamaster_id;
  const [comments, setComments] = useState<Comment[]>([]);

  const COMMENT_ITEM_HEIGHT = 72;
  const VISIBLE_COUNT = 5;
  const dispatch = useDispatch();
  const commentFormRef = useRef<CommentFormRef | null>(null);

  // ==========================================================
  // NORMALIZE COMMENTS
  // ==========================================================

  const normalizeComments = (commentsData: any[]): Comment[] => {
    return commentsData.map((comment) => ({
      user_id: Number(comment.user_id) || 0,
      first_name: comment.first_name ?? "GuestUser",
      vid_comment: comment.vid_comment ?? comment.comments ?? "",
      updated_on: comment.updated_on,
    }));
  };

  // ==========================================================
  // FETCH COMMENTS
  // ==========================================================

  const fetchInitialComments = useCallback(async () => {
    console.log("VTBCommentsSection - fetchInitialComments called");

    console.log("VTBCommentsSection - articleId:", articleId);

    if (!articleId) {
      console.log("VTBCommentsSection - No articleId, API will not be called");
      setComments([]);
      return;
    }

    try {
      const response = await axios.get(
        api.baseApi +
          `/api/get_article_comments_by_id/get_article_comments_by_id?article_id=${articleId}`,
      );

      console.log("VTBCommentsSection - Comments API response:", response.data);

      const fetched = response.data?.data?.[0];
      const commentCount = response.data?.data?.[0]?.length ?? 0;
      dispatch(updateNCGBrowserCommentsCount(commentCount));

      if (Array.isArray(fetched) && fetched.length > 0) {
        setComments(normalizeComments(fetched));
      } else {
        setComments([]);
      }
    } catch (error) {
      console.error("fetchInitialComments error:", error);
      setComments([]);
    }
  }, [articleId]);

  // ==========================================================
  // FETCH COMMENTS WHEN ARTICLE CHANGES
  // ==========================================================

  useEffect(() => {
    if (!articleId) {
      setComments([]);
      return;
    }
    fetchInitialComments();
  }, [fetchInitialComments]);

  useEffect(() => {
    if (focusCommentInput) {
      setTimeout(() => {
        commentFormRef.current?.focus();
      }, 500);
    }
  }, [focusCommentInput]);

  // ==========================================================
  // POST COMMENT
  // ==========================================================

  const postCommentUsage = async (comment: Comment) => {
    if (!articleId) return;

    const apiFormData = {
      data: [
        {
          customer_id: customerId,
          consortia_id: consortiaId,
          usermaster_id: usermasterId,
          article_id: articleId,
          status: true,
          comments: comment.vid_comment ?? "",
          // user_id: comment.user_id,
        },
      ],
    };

    const formData = new FormData();

    formData.append("detailed_json", JSON.stringify(apiFormData));

    try {
      await axios.post(
        api.baseApi +
          "/api/insert_video_coment_usage/insert_video_coment_usage?",
        formData,
      );
    } catch (error) {
      console.error("postCommentUsage error:", error);
      throw error;
    }
  };

  // ==========================================================
  // ADD COMMENT
  // ==========================================================

  const handleAddComment = async (newComment: Comment) => {
    // Optimistic UI update
    setComments((previousComments) => [newComment, ...previousComments]);

    try {
      await postCommentUsage(newComment);
      await fetchInitialComments();
    } catch (error) {
      console.error("handleAddComment error:", error);

      // Remove optimistic comment if API fails
      setComments((previousComments) =>
        previousComments.filter((comment) => comment !== newComment),
      );
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3 }}>
      <Typography variant="h6" mb={2}>
        {comments.length} Comments
      </Typography>

      <CommentForm onSubmit={handleAddComment} ref={commentFormRef} />

      <Box
        sx={{
          maxHeight: COMMENT_ITEM_HEIGHT * VISIBLE_COUNT,
          overflowY: "auto",
          position: "relative",
          marginTop: 1,
        }}
      >
        <CommentList comments={comments} />
      </Box>
    </Paper>
  );
};

export default VTBCommentsSection;
